/** What the 5/50 EMA trend rule is working with, and what it decided.
 *
 * Three reads with different lifetimes: `parameters` is a pre-market reference
 * derived from the date and three broker reads; `status` is the engine's live
 * runtime, which a restart empties; `decisions` is the durable trail.
 *
 * Every figure is in INDEX POINTS on the underlying, never option premium:
 * the stop, the ATR band and the trail are all measured on the index.
 *
 * Nulls are meaningful. A failed broker read leaves the field null with the
 * reason in `unavailable`, rather than substituting a stale number that would
 * end up sizing a real trade.
 */

import { getJson } from "./http";

export interface EmaTrendParameters {
  trade_date: string;
  /** Null at the weekend — there is no session, so the rule does not run. */
  underlying: string | null;
  itm_depth: number | null;
  strike_step: number | null;
  /** Depth in points, which is what you read off a chain. */
  itm_points: number | null;

  spot: number | null;
  vix: number | null;
  atr: number | null;

  /** The risk band: a stop must sit between these, in index points. */
  floor_points: number | null;
  cap_points: number | null;
  /** The ladder's two rungs. State 3 arms this rule's 5 EMA trail. */
  state2_points: number | null;
  state3_points: number | null;

  /** The same thresholds phrased as the questions asked at the chart: how big
   * must the candle be, how far before the 5 EMA takes over the exit, and how
   * far from the 50 EMA a reversal needs price to sit. */
  min_candle_points: number | null;
  min_stop_points: number | null;
  state2_pct: number | null;
  reversal_points: number | null;
  reversal_pct: number | null;
  /** Each distance as the index LEVEL it resolves to. Case 1 measures from
   * the entry — with nothing open, from spot, one level per side. Case 2
   * measures from the 50 EMA, which is sent alongside. */
  slow_ema_value: number | null;
  state2_level_long: number | null;
  state2_level_short: number | null;
  reversal_level_above: number | null;
  reversal_level_below: number | null;

  fast_ema: number;
  slow_ema: number;
  atr_period: number;
  /** Why a figure above is missing. Empty when everything resolved. */
  unavailable: string[];
}

export interface EmaTrendPosition {
  side: string;
  case: string;
  entry: number;
  stop: number;
  risk: number;
  /** Best price reached — what State 3 is measured against. */
  best: number;
  /** False until State 3. Until then the ONLY exit is the stop: the 5 EMA
   * trail and the reversal's 50 EMA target are both dead. */
  armed: boolean;
  contract_symbol: string | null;
  /** "CLEAR" / "BODY" on a REVERSAL: which signal-candle shape armed it.
   * Null on a TREND trade or a reversal adopted from a pre-2026-09-09 row. */
  shape: string | null;
}

export interface EmaTrendPending {
  side: string;
  case: string;
  limit: number;
  stop: number;
  risk: number;
  candles_left: number;
  /** True: `limit` is a trigger to BREAK (reversal). False: a limit to reach. */
  fills_on_break: boolean;
  /** "CLEAR" / "BODY" on a REVERSAL trigger; null on a TREND limit. */
  shape: string | null;
}

export interface EmaTrendStatus {
  /** False means no runtime since the last restart — NOT that the engine is
   * flat. A restart forgets an open position, so the trade log is the
   * authority on whether one exists. */
  tracking: boolean;
  trade_date: string | null;
  last_tick_at: string | null;
  structure: string | null;
  vix: number | null;
  last_spot: number | null;
  slow_ema: number | null;
  position: EmaTrendPosition | null;
  pending: EmaTrendPending | null;
}

export interface EmaTrendDecision {
  at: string;
  channel: string;
  message: string;
}

export const emaTrendApi = {
  parameters(): Promise<EmaTrendParameters> {
    return getJson<EmaTrendParameters>("/api/ema-trend/parameters");
  },
  status(strategyId: string): Promise<EmaTrendStatus> {
    return getJson<EmaTrendStatus>(
      `/api/ema-trend/status?strategy_id=${encodeURIComponent(strategyId)}`,
    );
  },
  decisions(strategyId: string): Promise<EmaTrendDecision[]> {
    return getJson<EmaTrendDecision[]>(
      `/api/ema-trend/decisions?strategy_id=${encodeURIComponent(strategyId)}`,
    );
  },
};
